import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Input, Space, Tag, Typography } from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ANS, SECS, XSEC, calcScores, isNC, type AnswerResult, type AppConfig, type AuditDetailDto, type QuestionDto, type ScoredSection } from "@audit-plan/shared";
import { api } from "../../api/client";
import { invalidateAudit } from "../../api/audits";
import { palette } from "../../theme";
import { fmtN } from "../pills";
import { UploadModal } from "../evidence/UploadModal";
import { useNotify } from "./useNotify";
import { useIsMobile } from "../useIsMobile";
import { AnswerModal } from "./AnswerModal";

const SHORT: Record<AnswerResult, string> = { C: "C", NC_MINOR: "NC mi", NC_MAJOR: "NC ma", NC_CRITICAL: "NC cr", OSS: "OSS", NA: "NA" };
const ANS_COLOR: Record<AnswerResult, string> = { C: palette.ok, NC_MINOR: palette.warn, NC_MAJOR: palette.bad, NC_CRITICAL: palette.crit, OSS: palette.primary, NA: palette.muted };
const SEC_COLOR: Record<string, string> = { S: palette.secS, H: palette.secH, E: palette.secE, X: palette.secX };

function byArea(qs: QuestionDto[]) {
  const g: { area: string; qs: QuestionDto[] }[] = [];
  for (const q of qs) {
    const last = g[g.length - 1];
    if (last && last.area === (q.area || "")) last.qs.push(q);
    else g.push({ area: q.area || "", qs: [q] });
  }
  return g;
}

export function ChecklistTab(props: { audit: AuditDetailDto; questions: QuestionDto[]; config: AppConfig; versionTitle?: string; onGoEvidence: () => void; /** false quando il tab non e' visibile: nasconde la barra fissa. */ active?: boolean }) {
  const { audit, questions, config, onGoEvidence } = props;
  const mobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  const qc = useQueryClient();
  const n = useNotify();
  const editable = audit.allowed.answer;
  const [modal, setModal] = useState<{ q: QuestionDto; r: AnswerResult } | null>(null);
  const [busyModal, setBusyModal] = useState(false);
  const [openComment, setOpenComment] = useState<Set<string>>(new Set());
  const [attachFor, setAttachFor] = useState<QuestionDto | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [curId, setCurId] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  const active = questions.filter((q) => q.active !== false);
  const scored = SECS.map((s) => ({ s, qs: active.filter((q) => q.section === s) }));
  const xq = active.filter((q) => q.section === XSEC);
  const sc = calcScores(active, audit.answers, config.weights);
  const text = (q: QuestionDto) => (en ? q.en || q.it : q.it);
  const attachOk = editable || (audit.allowed.upload && audit.status !== "closed");
  const order = [...scored.flatMap((x) => x.qs), ...xq];
  const isDone = (q: QuestionDto) => (q.section === XSEC ? !!audit.extra[q.id]?.v : !!audit.answers[q.id]?.r);
  const doneCount = order.filter(isDone).length;
  const curIdx = Math.max(0, order.findIndex((q) => q.id === curId));
  const curQ = order[curIdx];
  const evCount = (q: QuestionDto) => audit.evidence.filter((e) => e.target.type === "question" && e.target.id === q.id).length;


  // domanda corrente = l'ultima scheda il cui bordo alto ha superato l'intestazione
  useEffect(() => {
    if (!mobile) return;
    let raf = 0;
    const calc = () => {
      raf = 0;
      const cards = root.current?.querySelectorAll<HTMLElement>("[data-qid]");
      if (!cards?.length) return;
      let id = cards[0]!.dataset.qid!;
      for (const c of Array.from(cards)) {
        if (c.getBoundingClientRect().top <= 90) id = c.dataset.qid!;
        else break;
      }
      setCurId((p) => (p === id ? p : id));
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(calc);
    };
    calc();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => {
      window.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [mobile, order.length]);

  const jumpNext = useCallback(() => {
    const after = order.slice(curIdx + 1).find((q) => !isDone(q));
    const target = after ?? order.find((q) => !isDone(q));
    if (!target) return;
    const el = root.current?.querySelector<HTMLElement>(`[data-qid="${target.id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.map((q) => q.id + (isDone(q) ? 1 : 0)).join(), curIdx]);

  const setBusy = (id: string, on: boolean) =>
    setPending((p) => {
      const c = new Set(p);
      if (on) c.add(id);
      else c.delete(id);
      return c;
    });

  const put = async (q: QuestionDto, body: { r: AnswerResult; comment: string; ownerId?: string }) => {
    setBusy(q.id, true);
    // aggiornamento ottimistico
    qc.setQueryData<AuditDetailDto>(["audit", audit.id], (old) =>
      old ? { ...old, answers: { ...old.answers, [q.id]: { ...(old.answers[q.id] ?? { questionId: q.id, byId: null, ts: "", editedInValidation: false, prevR: null }), r: body.r, comment: body.comment } } } : old,
    );
    try {
      await api.put(`/audits/${audit.id}/answers/${q.id}`, body);
    } finally {
      setBusy(q.id, false);
      invalidateAudit(qc, audit.id);
    }
  };

  const answer = (q: QuestionDto, r: AnswerResult) => {
    if (!editable) return n.warn(t("notAllowed"));
    if (isNC(r) || r === "OSS") return setModal({ q, r });
    const prev = audit.answers[q.id];
    if (prev?.r === r) return;
    put(q, { r, comment: prev?.comment ?? "" }).catch(n.fail);
  };

  const submitModal = async (v: { comment: string; ownerId?: string }) => {
    if (!modal) return false;
    setBusyModal(true);
    try {
      await put(modal.q, { r: modal.r, comment: v.comment, ...(v.ownerId ? { ownerId: v.ownerId } : {}) });
      return true;
    } catch (e) {
      n.fail(e);
      return false;
    } finally {
      setBusyModal(false);
    }
  };

  const saveComment = (q: QuestionDto) => {
    const a = audit.answers[q.id];
    const v = drafts[q.id];
    if (!a?.r || v === undefined || v === (a.comment ?? "")) return;
    put(q, { r: a.r, comment: v }).catch(n.fail);
  };

  const extra = useMutation({
    mutationFn: ({ q, v, txt }: { q: QuestionDto; v: "Y" | "N"; txt: string }) => api.put(`/audits/${audit.id}/extra/${q.id}`, { v, text: txt }),
    onSuccess: () => invalidateAudit(qc, audit.id),
    onError: n.fail,
  });

  const banner =
    audit.status === "in_progress" ? null : audit.status === "in_validation" ? (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          editable
            ? en
              ? "Validation: as Audit Manager you can change the answers before validating. Every change is tracked."
              : "Validazione: come Audit Manager puoi modificare le risposte prima di validare. Ogni modifica resta tracciata."
            : en
              ? "Checklist under validation by the Audit Manager."
              : "Checklist in validazione dall'Audit Manager."
        }
      />
    ) : (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          ["planned", "confirmed"].includes(audit.status)
            ? en
              ? "The checklist can be filled in once the audit is started."
              : "La checklist si compila dopo l'avvio dell'audit."
            : en
              ? "Checklist locked: audit is past the execution phase."
              : "Checklist bloccata: l'audit ha superato la fase di esecuzione."
        }
      />
    );

  const sectionHead = (s: string, title: string, count: string, extraNode: React.ReactNode) => (
    <div style={{ display: "flex", alignItems: "center", gap: mobile ? 8 : 10, flexWrap: "wrap" }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: SEC_COLOR[s] }} />
      <b style={{ flex: 1, minWidth: 0, fontSize: mobile ? 14 : undefined }}>
        {title} <Typography.Text type="secondary">({count})</Typography.Text>
      </b>
      {extraNode}
    </div>
  );

  const ta = { fontSize: mobile ? 16 : undefined };
  const linkBtn = { padding: 0, height: mobile ? 32 : undefined };

  /** Righe di indicatori e azioni sotto il testo della domanda (stesse su desktop e telefono). */
  const indicators = (q: QuestionDto, scoredQ: boolean) => {
    const an = audit.answers[q.id];
    const nc = audit.ncs.find((x) => x.questionId === q.id);
    const needC = isNC(an?.r) || an?.r === "OSS";
    const nEv = evCount(q);
    return (
      <Space size={mobile ? 12 : 10} wrap style={{ fontSize: 12, marginTop: 4 }}>
        {scoredQ ? (
          <Typography.Text type="secondary">
            {q.points || 10} {t("points")}
          </Typography.Text>
        ) : null}
        {nEv ? (
          <Button type="link" size="small" style={linkBtn} onClick={onGoEvidence}>
            {nEv} {t("files")}
          </Button>
        ) : null}
        {scoredQ && nc ? <Typography.Text type="secondary">{t("ncCreated")} {nc.code}</Typography.Text> : null}
        {scoredQ && an?.r === "OSS" ? <Tag color="blue">{t("ans_OSS")}</Tag> : null}
        {scoredQ && an?.editedInValidation ? <Tag color="gold">{t("editedInValidation")}</Tag> : null}
        {scoredQ && editable && !needC && !an?.comment && an?.r && !openComment.has(q.id) ? (
          <Button type="link" size="small" style={linkBtn} onClick={() => setOpenComment((p) => new Set(p).add(q.id))}>
            + {t("comment")}
          </Button>
        ) : null}
        {attachOk ? (
          <Button type="link" size="small" style={linkBtn} onClick={() => setAttachFor(q)}>
            + {t("attach")}
          </Button>
        ) : null}
        {scoredQ && editable && needC ? (
          <Button type="link" size="small" style={linkBtn} onClick={() => setModal({ q, r: an!.r })}>
            {t("edit")}
          </Button>
        ) : null}
      </Space>
    );
  };

  const comments = (q: QuestionDto) => {
    const an = audit.answers[q.id];
    const needC = isNC(an?.r) || an?.r === "OSS";
    const showTa = !needC && editable && (openComment.has(q.id) || !!an?.comment);
    return (
      <>
        {needC && an?.comment ? <div style={{ marginTop: 6, whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: palette.fg, background: palette.sunk, padding: "6px 10px", borderRadius: 6 }}>{an.comment}</div> : null}
        {showTa || (!editable && !needC && an?.comment) ? (
          <Input.TextArea
            style={{ marginTop: 6, ...ta }}
            autoSize={{ minRows: 2 }}
            readOnly={!editable}
            placeholder={t("comment")}
            value={drafts[q.id] ?? an?.comment ?? ""}
            onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))}
            onBlur={() => saveComment(q)}
          />
        ) : null}
      </>
    );
  };

  const answerBtns = (q: QuestionDto) => {
    const an = audit.answers[q.id];
    const btn = (r: AnswerResult) => {
      const on = an?.r === r;
      return (
        <Button
          key={r}
          size={mobile ? "large" : "small"}
          title={t(`ans_${r}`)}
          aria-pressed={on}
          disabled={!editable || pending.has(q.id)}
          onClick={() => answer(q, r)}
          style={
            mobile
              ? { height: 48, minWidth: 0, padding: "0 4px", fontSize: 15, fontWeight: 700, ...(on ? { background: ANS_COLOR[r], borderColor: ANS_COLOR[r], color: "#fff" } : { color: ANS_COLOR[r], borderColor: palette.hair }) }
              : on
                ? { background: ANS_COLOR[r], borderColor: ANS_COLOR[r], color: "#fff", fontWeight: 700 }
                : { fontWeight: 600 }
          }
        >
          {SHORT[r]}
        </Button>
      );
    };
    if (mobile) return <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8, marginTop: 10 }}>{ANS.map(btn)}</div>;
    return <Space.Compact>{ANS.map(btn)}</Space.Compact>;
  };

  const yesNoBtns = (q: QuestionDto, draft: string | undefined) => {
    const x = audit.extra[q.id];
    const b = (v: "Y" | "N") => (
      <Button
        key={v}
        size={mobile ? "large" : "small"}
        disabled={!editable}
        aria-pressed={x?.v === v}
        onClick={() => extra.mutate({ q, v, txt: draft ?? x?.text ?? "" })}
        style={{ ...(mobile ? { height: 48, fontSize: 15 } : {}), ...(x?.v === v ? { background: v === "Y" ? palette.ok : palette.bad, borderColor: v === "Y" ? palette.ok : palette.bad, color: "#fff", fontWeight: 700 } : { fontWeight: 600 }) }}
      >
        {t(`ans_${v}`)}
      </Button>
    );
    if (mobile) return <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8, marginTop: 10 }}>{(["Y", "N"] as const).map(b)}</div>;
    return <Space.Compact>{(["Y", "N"] as const).map(b)}</Space.Compact>;
  };

  const areaBand = (area: string) =>
    area && !mobile ? <div style={{ padding: "8px 16px", background: palette.sunk, fontWeight: 700, fontSize: 12, letterSpacing: 0.3, textTransform: "uppercase", color: palette.muted }}>{area}</div> : null;

  const cardStyle: React.CSSProperties = { padding: "12px 12px 14px", borderTop: `1px solid ${palette.hair}`, scrollMarginTop: 76, minWidth: 0 };

  const scoredQuestion = (q: QuestionDto) => {
    const an = audit.answers[q.id];
    if (mobile)
      return (
        <div key={q.id} data-qid={q.id} style={{ ...cardStyle, background: an?.r ? undefined : "#FFFDF8" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12, color: palette.muted, marginBottom: 4, minWidth: 0 }}>
            <span style={{ fontFamily: "var(--ap-mono)", fontWeight: 700, color: palette.fg }}>{q.number || q.code}</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.area}</span>
          </div>
          <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{text(q)}</div>
          {answerBtns(q)}
          {indicators(q, true)}
          {comments(q)}
        </div>
      );
    return (
      <div key={q.id} style={{ display: "grid", gridTemplateColumns: "56px 1fr auto", gap: 12, padding: "12px 16px", borderTop: `1px solid ${palette.hair}`, alignItems: "start" }}>
        <div style={{ fontFamily: "var(--ap-mono)", color: palette.muted }}>{q.number || q.code}</div>
        <div>
          <div style={{ fontWeight: 600 }}>{text(q)}</div>
          {indicators(q, true)}
          {comments(q)}
        </div>
        {answerBtns(q)}
      </div>
    );
  };

  const extraQuestion = (q: QuestionDto) => {
    const x = audit.extra[q.id];
    const draft = drafts["x" + q.id];
    const area = (
      <>
        {editable || x?.text ? (
          <Input.TextArea
            style={{ marginTop: 6, ...ta }}
            autoSize={{ minRows: 2 }}
            readOnly={!editable}
            placeholder={t("evidenceText")}
            value={draft ?? x?.text ?? ""}
            onChange={(e) => setDrafts((d) => ({ ...d, ["x" + q.id]: e.target.value }))}
            onBlur={() => {
              if (draft !== undefined && x?.v && draft !== (x.text ?? "")) extra.mutate({ q, v: x.v, txt: draft });
            }}
          />
        ) : null}
      </>
    );
    if (mobile)
      return (
        <div key={q.id} data-qid={q.id} style={{ ...cardStyle, background: x?.v ? undefined : "#FFFDF8" }}>
          <div style={{ fontFamily: "var(--ap-mono)", fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{q.number}</div>
          <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{text(q)}</div>
          {yesNoBtns(q, draft)}
          {indicators(q, false)}
          {area}
        </div>
      );
    return (
      <div key={q.id} style={{ display: "grid", gridTemplateColumns: "56px 1fr auto", gap: 12, padding: "12px 16px", borderTop: `1px solid ${palette.hair}`, alignItems: "start" }}>
        <div style={{ fontFamily: "var(--ap-mono)", color: palette.muted }}>{q.number}</div>
        <div>
          <div style={{ fontWeight: 600 }}>{text(q)}</div>
          {indicators(q, false)}
          {area}
        </div>
        {yesNoBtns(q, draft)}
      </div>
    );
  };

  const secTitle = (q?: QuestionDto) => (q ? (q.section === XSEC ? t("extraInfo") : t(`sec_${q.section}`)) : "");
  const showBar = mobile && editable && props.active !== false && order.length > 0;

  return (
    <div ref={root} style={showBar ? { paddingBottom: 76 } : undefined}>
      {banner}
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <Typography.Text type="secondary">
          {props.versionTitle ?? ""} · {en ? "all questions are mandatory" : "tutte le domande sono obbligatorie"}
        </Typography.Text>
        {mobile ? null : <Typography.Text type="secondary">C · NC minor · NC major · NC critical · OSS · NA</Typography.Text>}
      </div>
      <Space direction="vertical" size={mobile ? 12 : 16} style={{ width: "100%" }}>
        {scored.map(({ s, qs }) => (
          <Card
            key={s}
            size={mobile ? "small" : "default"}
            title={sectionHead(s, t(`sec_${s}`), `${qs.filter((q) => audit.answers[q.id]?.r).length}/${qs.length}`, (
              <>
                {mobile ? null : (
                  <Typography.Text type="secondary">
                    {t("weight")} {config.weights[s as ScoredSection]}%
                  </Typography.Text>
                )}
                <Tag style={{ marginInlineEnd: 0 }}>
                  {t("sectionScore")}: <b>{sc[s as ScoredSection] == null ? "—" : fmtN(sc[s as ScoredSection], 1)}</b>
                </Tag>
              </>
            ))}
            styles={{ body: { padding: 0 } }}
          >
            {byArea(qs).map((g) => (
              <div key={g.area}>
                {areaBand(g.area)}
                {g.qs.map(scoredQuestion)}
              </div>
            ))}
          </Card>
        ))}
        <Card size={mobile ? "small" : "default"} title={sectionHead("X", t("extraInfo"), `${xq.filter((q) => audit.extra[q.id]?.v).length}/${xq.length}`, <Tag style={{ marginInlineEnd: 0 }}>{t("notScored")}</Tag>)} styles={{ body: { padding: 0 } }}>
          {byArea(xq).map((g) => (
            <div key={g.area}>
              {areaBand(g.area)}
              {g.qs.map(extraQuestion)}
            </div>
          ))}
        </Card>
      </Space>
      {showBar ? (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 30,
            background: palette.card,
            borderTop: `1px solid ${palette.hair}`,
            boxShadow: "0 -2px 10px rgba(0,0,0,.08)",
            padding: "8px 12px calc(8px + env(safe-area-inset-bottom))",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
            <div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{secTitle(curQ)}</div>
            <div style={{ fontSize: 12, color: palette.muted }}>{t("b_answered_of", { a: doneCount, t: order.length })}</div>
          </div>
          <Button type="primary" size="large" icon={<ArrowDownOutlined />} style={{ height: 48 }} disabled={doneCount === order.length} onClick={jumpNext}>
            {doneCount === order.length ? t("b_all_done") : t("b_next_todo")}
          </Button>
        </div>
      ) : null}
      <AnswerModal open={!!modal} onClose={() => setModal(null)} audit={audit} question={modal?.q ?? null} result={modal?.r ?? null} busy={busyModal} onSubmit={submitModal} />
      <UploadModal
        open={!!attachFor}
        onClose={() => setAttachFor(null)}
        ctx={{ supplierId: audit.supplierId, auditId: audit.id, target: attachFor ? { type: "question", id: attachFor.id } : undefined }}
      />
    </div>
  );
}
